'use client';

import dynamic from 'next/dynamic';
import { useEffect, useMemo, useState } from 'react';
import cn from '@/lib/packages/cn';

type Props = {
  fullPlaybackId: string;
  shortPlaybackId?: string; // without it there is no Full/Short toggle and the full video always plays
  title?: string;
  className?: string;
  aspectRatio?: string; // e.g. '16/9'
  posterSecond?: number; // overrides the default poster frame (30 s full / 10.5 s short)
  posterWidth?: number; // poster width in px (1200 before the player mounts, MuxVideo's default after)
};

export default function PressVideoTabs({
  fullPlaybackId,
  shortPlaybackId,
  title = 'Video',
  className,
  aspectRatio = '16/9',
  posterSecond: posterSecondOverride,
  posterWidth,
}: Props) {
  const [tab, setTab] = useState<'full' | 'short'>('full');
  const [isMounted, setIsMounted] = useState(false);
  const hasTabs = Boolean(shortPlaybackId);
  const isFull = !hasTabs || tab === 'full';
  const playbackId = isFull ? fullPlaybackId : (shortPlaybackId ?? fullPlaybackId);
  const posterSecond = posterSecondOverride ?? (isFull ? 30 : 10.5);
  // poster still computed to pass as placeholder to Mux for initial frame
  const posterUrl = useMemo(
    () =>
      `https://image.mux.com/${playbackId}/thumbnail.jpg?time=${posterSecond}&width=${posterWidth ?? 1200}&format=webp`,
    [playbackId, posterSecond, posterWidth],
  );
  const MuxVideo = dynamic(() => import('@/components/ui/MuxVideo'), { ssr: false });

  useEffect(() => {
    // Defer mounting the player until after first client paint to avoid
    // any hydration hiccups on certain mobile browsers.
    const id = window.requestAnimationFrame(() => setIsMounted(true));
    return () => window.cancelAnimationFrame(id);
  }, []);

  return (
    <div className={cn('w-full', className)}>
      {hasTabs ? (
        <div className='mb-3 flex items-center justify-center sm:justify-end'>
          <div
            role='tablist'
            aria-label='Video version'
            className='relative inline-flex w-[180px] select-none items-center justify-between overflow-hidden rounded-full bg-gray-100 p-1 text-sm/6 dark:bg-white/10'
            onKeyDown={(e) => {
              if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                e.preventDefault();
                setTab((prev) => (prev === 'full' ? 'short' : 'full'));
              }
            }}
          >
            <span
              className={cn(
                'pointer-events-none absolute inset-y-1 left-1 w-[calc(50%-0.5rem)] rounded-full bg-white shadow-sm ring-1 ring-black/5 transition-transform duration-200 ease-out dark:bg-gray-800 dark:ring-white/10',
                tab === 'short' ? 'translate-x-[calc(100%+0.5rem)]' : 'translate-x-0',
              )}
              aria-hidden='true'
            />
            <button
              type='button'
              role='tab'
              aria-selected={tab === 'full'}
              className={cn(
                'relative z-10 w-1/2 cursor-pointer rounded-full px-3 py-1.5 font-medium transition-colors',
                tab === 'full' ? 'text-secondary' : 'text-secondary-foreground',
              )}
              onClick={() => setTab('full')}
            >
              Full
            </button>
            <button
              type='button'
              role='tab'
              aria-selected={tab === 'short'}
              className={cn(
                'relative z-10 w-1/2 cursor-pointer rounded-full px-3 py-1.5 font-medium transition-colors',
                tab === 'short' ? 'text-secondary' : 'text-secondary-foreground',
              )}
              onClick={() => setTab('short')}
            >
              Short
            </button>
          </div>
        </div>
      ) : null}
      <div
        className='relative w-full overflow-hidden rounded-md ring-1 ring-gray-900/10 dark:ring-white/10'
        style={{ aspectRatio }}
      >
        {isMounted ? (
          <MuxVideo
            playbackId={playbackId}
            title={title}
            className='h-full w-full'
            aspectRatio={aspectRatio}
            posterSecond={posterSecond}
            posterWidth={posterWidth}
            preload='none'
          />
        ) : (
          // biome-ignore lint/performance/noImgElement: Mux poster fallback shown briefly before MuxVideo mounts
          <img src={posterUrl} alt={title} className='h-full w-full object-cover' decoding='async' loading='eager' />
        )}
      </div>
    </div>
  );
}
